import React, { useState } from 'react';
import { HelpCircle, X, ChevronRight, ChevronLeft, Lightbulb, MousePointer, Calculator, Save } from 'lucide-react';

const tutorialSteps = [
  {
    icon: <Lightbulb size={48} color="#3a86ff" />,
    title: 'Bienvenue !',
    content: `Cette application vous aide à optimiser les découpes de plaques de placoplatre pour vos projets de rénovation ou construction.`
  },
  {
    icon: <MousePointer size={48} color="#3a86ff" />,
    title: 'Configuration des murs',
    content: `Commencez par configurer les dimensions de vos murs. Vous pouvez ajouter plusieurs murs et définir leurs dimensions (largeur et hauteur).`
  },
  {
    icon: <MousePointer size={48} color="#06d6a0" />,
    title: 'Ajout d\'ouvertures',
    content: `Ajoutez des ouvertures (portes, fenêtres, prises, etc.) en cliquant sur "Ajouter une ouverture". Vous pouvez déplacer les ouvertures par glisser-déposer directement sur le plan.`
  },
  {
    icon: <Calculator size={48} color="#ffbe0b" />,
    title: 'Calcul d\'optimisation',
    content: `Cliquez sur "Calculer l\'optimisation" pour générer le plan de découpe optimal. L\'algorithme minimise les chutes et réutilise les morceaux lorsque c\'est possible.`
  },
  {
    icon: <Save size={48} color="#ef476f" />,
    title: 'Sauvegarde et export',
    content: `Sauvegardez vos projets pour les retrouver plus tard. Vous pouvez aussi exporter les résultats en PDF pour les emmener sur le chantier.`
  }
];

const tips = [
 {
    title: 'Optimisation des chutes',
    content: 'Activez l\'option "Utiliser les chutes" pour réutiliser automatiquement les morceaux de plaques restants. Cela peut réduire considérablement le nombre de plaques nécessaires.'
  },
  {
    title: 'Déplacement des ouvertures',
    content: 'Vous pouvez déplacer les ouvertures directement sur le plan en cliquant et en les faisant glisser. L\'origine (0,0) est en bas à gauche du mur.'
  },
  {
    title: 'Vue 3D',
    content: 'Utilisez la vue 3D pour visualiser l\'ensemble de votre pièce. Double-cliquez sur un mur dans la vue globale pour l\'éditer.'
  },
  {
    title: 'Types d\'ouvertures',
    content: 'Pour les tuyaux, vous pouvez ajuster le diamètre. Pour les prises et interrupteurs, vous pouvez configurer le nombre d\'éléments et leur disposition.'
  },
  {
    title: 'Calcul du coût',
    content: 'Utilisez le bouton "Coût" pour estimer le prix total de votre projet en fonction du prix des plaques et de la main d\'œuvre.'
  }
];

function HelpModal({ className = '' }) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('tutorial');
  const [currentStep, setCurrentStep] = useState(0);

  const nextStep = () => {
    if (currentStep < tutorialSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const closeModal = () => {
    setIsOpen(false);
    setCurrentStep(0);
    setActiveTab('tutorial');
  };

  return (
    <div className={className}>
      <button 
        className="btn btn-secondary"
        onClick={() => setIsOpen(true)}
        title="Aide"
      >
        <HelpCircle size={18} style={{ marginRight: '8px' }} />
        Aide
      </button>

      {isOpen && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-container" style={{ maxWidth: '600px' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Centre d'aide</h3>
              <button className="btn-icon" onClick={closeModal}>
                <X size={20} />
              </button>
            </div>

            <div className="modal-body">
              {/* Onglets */}
              <div className="help-tabs" style={{
                display: 'flex',
                borderBottom: '1px solid #eee',
                marginBottom: '20px'
              }}>
                <button
                  className={`help-tab ${activeTab === 'tutorial' ? 'active' : ''}`}
                  onClick={() => setActiveTab('tutorial')}
                  style={{
                    padding: '10px 20px',
                    background: 'none',
                    border: 'none',
                    borderBottom: activeTab === 'tutorial' ? '2px solid #3a86ff' : '2px solid transparent',
                    color: activeTab === 'tutorial' ? '#3a86ff' : '#666',
                    cursor: 'pointer',
                    fontWeight: activeTab === 'tutorial' ? 'bold' : 'normal'
                  }}
                >
                  Tutoriel
                </button>
                <button
                  className={`help-tab ${activeTab === 'tips' ? 'active' : ''}`}
                  onClick={() => setActiveTab('tips')}
                  style={{
                    padding: '10px 20px',
                    background: 'none',
                    border: 'none',
                    borderBottom: activeTab === 'tips' ? '2px solid #3a86ff' : '2px solid transparent',
                    color: activeTab === 'tips' ? '#3a86ff' : '#666',
                    cursor: 'pointer',
                    fontWeight: activeTab === 'tips' ? 'bold' : 'normal'
                  }}
                >
                  Astuces
                </button>
              </div>

              {/* Contenu du tutoriel */}
              {activeTab === 'tutorial' && (
                <div className="tutorial-content">
                  <div className="tutorial-step" style={{
                    textAlign: 'center',
                    padding: '20px'
                  }}>
                    <div className="step-icon" style={{ marginBottom: '20px' }}>
                      {tutorialSteps[currentStep].icon}
                    </div>
                    <h4 style={{ marginBottom: '15px', fontSize: '1.3em' }}>
                      {tutorialSteps[currentStep].title}
                    </h4>
                    <p style={{ 
                      color: '#666', 
                      lineHeight: '1.6',
                      fontSize: '1.05em'
                    }}>
                      {tutorialSteps[currentStep].content}
                    </p>
                  </div>

                  {/* Indicateur de progression */}
                  <div className="step-indicator" style={{
                    display: 'flex',
                    justifyContent: 'center',
                    gap: '8px',
                    margin: '20px 0'
                  }}>
                    {tutorialSteps.map((_, index) => (
                      <div
                        key={index}
                        style={{
                          width: '10px',
                          height: '10px',
                          borderRadius: '50%',
                          backgroundColor: index === currentStep ? '#3a86ff' : '#ddd',
                          transition: 'background-color 0.3s'
                        }}
                      />
                    ))}
                  </div>

                  {/* Navigation */}
                  <div className="tutorial-nav" style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginTop: '20px'
                  }}>
                    <button
                      className="btn btn-secondary"
                      onClick={prevStep}
                      disabled={currentStep === 0}
                      style={{ opacity: currentStep === 0 ? 0.5 : 1 }}
                    >
                      <ChevronLeft size={18} style={{ marginRight: '5px' }} />
                      Précédent
                    </button>
                    <button
                      className="btn btn-primary"
                      onClick={nextStep}
                      disabled={currentStep === tutorialSteps.length - 1}
                      style={{ opacity: currentStep === tutorialSteps.length - 1 ? 0.5 : 1 }}
                    >
                      Suivant
                      <ChevronRight size={18} style={{ marginLeft: '5px' }} />
                    </button>
                  </div>
                </div>
              )}

              {/* Contenu des astuces */}
              {activeTab === 'tips' && (
                <div className="tips-content">
                  {tips.map((tip, index) => (
                    <div
                      key={index}
                      className="tip-item"
                      style={{
                        backgroundColor: '#f8f9fa',
                        borderRadius: '8px',
                        padding: '15px',
                        marginBottom: '12px',
                        borderLeft: '3px solid #3a86ff'
                      }}
                    >
                      <h5 style={{ 
                        margin: '0 0 8px 0', 
                        color: '#3a86ff',
                        fontSize: '1em'
                      }}>
                        💡 {tip.title}
                      </h5>
                      <p style={{ 
                        margin: 0, 
                        color: '#666',
                        fontSize: '0.95em',
                        lineHeight: '1.5'
                      }}>
                        {tip.content}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default HelpModal;
